import { useTranslation } from "react-i18next"

import type { SharePointSite } from "../types"

import { Folder, FolderOpen } from "@/components/icons"

import { sortFiles } from "../utils"
import { formatFileDateTime } from "../utils/date-formatting"

interface SharePointSitesProps {
  sites: SharePointSite[]
  sortKey: "name" | "modified"
  sortDir: "asc" | "desc"
  onSelectSite: (site: SharePointSite) => void
}

export function SharePointSites({ sites, sortKey, sortDir, onSelectSite }: SharePointSitesProps) {
  const { t } = useTranslation()

  return (
    <div className="flex h-full flex-col gap-3 px-1 sm:gap-4 sm:px-0">
      <div className="flex flex-shrink-0 items-center gap-2 border-b pb-2">
        <FolderOpen className="text-primary h-4 w-4 sm:h-5 sm:w-5" />
        <h3 className="text-xs font-medium sm:text-base">
          {t("filepicker.sharepoint_sites", { defaultValue: "SharePoint Sites" })}
        </h3>
      </div>
      <div className="flex-1 overflow-auto">
        <div className="space-y-2 sm:space-y-3">
          {sites.length > 0 ? (
            sortFiles(
              sites,
              (s) => (sortKey === "name" ? s.displayName : s.lastModified),
              sortDir
            ).map((site) => (
              <button
                key={site.siteId}
                className="group bg-card hover:bg-accent/50 focus-visible:ring-ring w-full cursor-pointer rounded-lg border p-3 text-left transition-colors focus-visible:ring-1 focus-visible:outline-none sm:p-4"
                type="button"
                onClick={() => onSelectSite(site)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-foreground text-xs font-medium sm:text-sm">
                      {site.displayName}
                    </h4>
                    <div className="text-muted-foreground mt-1 flex items-center gap-2 text-[10px] sm:text-xs">
                      <span>
                        {t("filepicker.libraries_count", {
                          defaultValue: "{{count}} libraries",
                          count: site.drivesCount,
                        })}
                      </span>
                      {site.lastModified && (
                        <>
                          <span>•</span>
                          <span>{formatFileDateTime(site.lastModified)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Folder className="text-muted-foreground group-hover:text-primary h-5 w-5 transition-colors" />
                </div>
              </button>
            ))
          ) : (
            <div className="flex h-full items-center justify-center py-12">
              <p className="text-muted-foreground text-sm">
                {t("filepicker.no_sharepoint_sites", {
                  defaultValue: "No SharePoint sites available",
                })}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
